'use client';

import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import styled from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';
import { Icon } from '@liftango/ui-icons';
import { useLabels } from '@liftango/ui-tokens';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  width?: number;
  /**
   * Optional footer node — typically a row of action `Button`s. Pinned to the
   * bottom of the drawer with a top border. When omitted, the body fills the
   * full vertical space.
   */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const Overlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: ${(p) => p.theme.zIndex.drawer};
`;

const Content = styled(Dialog.Content)<{ $width: number }>`
  position: fixed;
  top: 0;
  inset-inline-end: 0;
  bottom: 0;
  width: ${(p) => p.$width}px;
  background: ${(p) => p.theme.colors.neutral.panel};
  border-inline-start: 1px solid ${(p) => p.theme.colors.neutral.line};
  box-shadow: ${(p) => p.theme.elevation.overlay.shadow};
  display: flex;
  flex-direction: column;
  z-index: ${(p) => p.theme.zIndex.drawer};
  outline: none;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(p) => p.theme.spaceAlias.md} ${(p) => p.theme.spaceAlias.lg};
  border-bottom: 1px solid ${(p) => p.theme.colors.neutral.line};
`;

const Title = styled(Dialog.Title)`
  margin: 0;
  font-size: ${(p) => p.theme.fontSize.lg};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  font-family: ${(p) => p.theme.fontFamily.ui};
  color: ${(p) => p.theme.colors.neutral.ink};
`;

const CloseBtn = styled.button`
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  cursor: pointer;
  border-radius: ${(p) => p.theme.radius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: ${(p) => p.theme.colors.neutral.line};
  }
  ${focusRing}
`;

const Body = styled.div`
  flex: 1;
  overflow: auto;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${(p) => p.theme.spaceAlias.sm};
  padding: ${(p) => p.theme.spaceAlias.md} ${(p) => p.theme.spaceAlias.lg};
  border-top: 1px solid ${(p) => p.theme.colors.neutral.line};
  background: ${(p) => p.theme.colors.neutral.panel};
`;

export function Drawer({ open, onClose, title, width = 380, footer, children }: DrawerProps) {
  const labels = useLabels();
  const closeLabel = labels.actions?.close ?? 'Close';
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <Dialog.Portal>
        <Overlay />
        <Content $width={width}>
          <Header>
            <Title>{title}</Title>
            <Dialog.Close asChild>
              <CloseBtn aria-label={closeLabel}>
                <Icon name="xmark" />
              </CloseBtn>
            </Dialog.Close>
          </Header>
          <Body>{children}</Body>
          {footer !== undefined && <Footer data-testid="drawer-footer">{footer}</Footer>}
        </Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
